import React from "react";
import { Popover } from "antd";
import { Users, CalendarRange, ChevronDown, Activity as ActivityIcon, CalendarClock } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDashTheme } from "../hooks/useDashTheme";
import { useClassicOverview } from "../hooks/useClassicOverview";
import { useLiveTeamHours } from "../hooks/useLiveTeamHours";
import { useClassicFilters, subjectPath, toolPath, classicPath, dayPath, fmtDay, orgTodayPath, alertPath } from "../components/drilldown/drilldownRoutes";
import { useAuth } from "../../../contexts/AuthContext";
import { ClassicMotion } from "../components/classic/micro";
import { DateRange, rangeForPreset } from "../components/DateRange";
import { ToolSelect } from "../components/ToolSelect";
import { useApi } from "../../../hooks/useApi";
import { HeroBand } from "./graphical/HeroBand";
import { GPanel } from "./graphical/parts";
import { ClockPanel, ToolsPanel, ProjectsPanelG, AlertsPanel, NewsPanel, PeoplePanel, AiAdoptionPanel } from "./graphical/Panels";
import { useGraphicalPeople } from "./graphical/useGraphicalPeople";
import { useGraphicalAiAdoption } from "../hooks/useGraphicalAiAdoption";
import { WowMotion } from "./graphical/WowMotion";
import { MonoTile, inks, paper, isSingleDay, prettyDay } from "./graphical/viz";
// ADDITIVE — the three header actions this page shares with /dashboard/classic.
// Each is self-gating: the module decides whether this org/viewer may see it.
import { ShiftTimelineDrawer, canUserAccessShiftTimeline } from "../../../modules/stealth-clock";
import { ShiftChip } from "./graphical/ShiftChip";
// 2026-08-30 — Graphical ↔ Brain switch, shared with BrainV2.
import { DashViewSwitch } from "../components/DashViewSwitch";
import { useOrgProfile } from "../hooks/useOrgProfile";
// Third-party services wear their own marks — a grey envelope beside "0/15
// Gmail" makes people ask WHICH mail. Everything TeamGrid owns keeps a line
// glyph, tinted with a hue this app already assigns a meaning to.
import { GmailMark } from "./graphical/BrandMarks";
import { EmployeeActivityModal, canUserAccessEmployeeActivity } from "../../../modules/employee-activity";
import { GmailModal } from "../components/GmailModal";
import { IdleLogsModal } from "../components/IdleLogsModal";
import { StealthIdleReasonModal } from "../components/StealthIdleReasonModal";
import { getOrgGmailStatus } from "../../../modules/email-insights/engine/backendClient";

const PERIOD_PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "7 days" },
];

export default function GraphicalDashboard() {
  const t = useDashTheme();
  const k = inks(t);
  const { user } = useAuth();
  const isLead = user && (user.role === "org_admin" || user.role === "main_admin");
  const navigate = useNavigate();
  const api = useApi();
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Header actions (Activity · Shift Timeline · Gmail) ──────────────────
  // Reactive, not a bare `window.__orgProfile` read: Layout sets that inside an
  // async fetch and dispatches nothing, so a synchronous read during render races
  // it. Losing that race hid these two buttons for the whole session.
  const orgProfile = useOrgProfile();
  const [employeeActivityOpen, setEmployeeActivityOpen] = React.useState(false);
  const showEmployeeActivity = canUserAccessEmployeeActivity(user, orgProfile);
  const [shiftTimelineOpen, setShiftTimelineOpen] = React.useState(false);
  const showShiftTimeline = canUserAccessShiftTimeline(user, orgProfile);

  const [gmailOpen, setGmailOpen] = React.useState(false);
  const [gmail, setGmail] = React.useState(null);
  const [gmailLoading, setGmailLoading] = React.useState(true);
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try { const res = await getOrgGmailStatus(); if (alive) setGmail(res || null); }
      catch (_e) { if (alive) setGmail(null); }
      finally { if (alive) setGmailLoading(false); }
    })();
    return () => { alive = false; };
  }, []);
  // A dead "0/0" chip is worse than no chip — hide once we know there is nothing.
  const showGmail = gmailLoading || !!gmail;
  const gmailConnected = gmail?.connectedCount ?? 0;
  const gmailTotal = gmail?.total ?? (gmail?.members?.length || 0);
  const gmailState = (!gmailTotal || !gmailConnected) ? "none"
    : gmailConnected === gmailTotal ? "all" : "some";

  // ── Idle drill-down ──────────────────────────────────────────────────────
  // Same two-path behaviour as /dashboard/classic: when the org has opted into
  // stealth idle reasons the employee-reason modal opens, otherwise the plain
  // idle-logs modal does. The reason modal can hand back to the regular one.
  const [idleTarget, setIdleTarget] = React.useState(null);
  const [stealthIdleTarget, setStealthIdleTarget] = React.useState(null);
  const openIdle = React.useCallback((person) => {
    if (!person?.userId) return;
    const target = { userId: person.userId, name: person.name };
    if (window.__orgProfile?.stealthIdleReasonEnabled === true) setStealthIdleTarget(target);
    else setIdleTarget(target);
  }, []);
  const fallbackToRegularIdle = React.useCallback(() => {
    setStealthIdleTarget((prev) => { if (prev) setIdleTarget(prev); return null; });
  }, []);

  const seeded = useClassicFilters();
  const [range, setRange] = React.useState(seeded.range);
  // Same default as card V2 (`useDashboardData`): admins open on the whole
  // scope. Everyone else opens on THEMSELVES. An org_user's null-scope is
  // self + lower levels + reportees, so "All team members" sums the tree
  // (Nitin BDE → 6 people → 21h) and reads as inflated personal hours.
  const [member, setMember] = React.useState(() => {
    if (seeded.member) return seeded.member;
    if (user && !isLead) return user.id || user._id || null;
    return null;
  });
  const memberTouched = React.useRef(!!seeded.member);
  React.useEffect(() => {
    if (memberTouched.current) return;
    if (!user?.id && !user?._id) return;
    memberTouched.current = true;
    if (isLead) return;
    setMember(user.id || user._id);
  }, [user, isLead]);
  const selectMember = React.useCallback((v) => {
    memberTouched.current = true;
    setMember(v);
  }, []);

  React.useEffect(() => {
    const p = new URLSearchParams(searchParams);
    if (range?.start) p.set("start", fmtDay(range.start));
    if (range?.end) p.set("end", fmtDay(range.end));
    if (range?.key) p.set("preset", range.key);
    if (member) p.set("member", String(member)); else p.delete("member");
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, member]);

  const [members, setMembers] = React.useState([]);
  React.useEffect(() => {
    let alive = true;
    api.get("/api/v1/users/team-members-dropdown")
      .then((m) => {
        if (!alive || !m.data?.success) return;
        const list = m.data.data || m.data.members || [];
        setMembers(list.map((u) => ({
          value: u.userId || u._id || u.id,
          label: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "Member",
          sub: u.position || u.email,
        })));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [api]);

  const memberOptions = [{ value: null, label: "All team members" }, ...members];
  const [periodOpen, setPeriodOpen] = React.useState(false);
  const { overview, loading, error } = useClassicOverview(range, member);
  const { livePeople, liveLoading } = useLiveTeamHours(range, member);
  const peopleData = useGraphicalPeople(range, member);
  const aiAdopt = useGraphicalAiAdoption(range, member);

  const go = React.useCallback((path) => { if (path) navigate(path); }, [navigate]);
  const openSubject = React.useCallback(
    (subject) => go(subjectPath(subject, range, member)),
    [go, range, member]
  );
  const openTool = React.useCallback(
    (tool) => go(toolPath({ name: tool.name, type: tool.type }, range, member)),
    [go, range, member]
  );
  const openProject = React.useCallback(
    (p) => p?.projectId && openSubject({ kind: "project", id: p.projectId, name: p.name }),
    [openSubject]
  );
  const openAlert = React.useCallback(
    (row) => go(alertPath(row, range, member)),
    [go, range, member]
  );

  const ppr = paper(t);
  const single = isSingleDay(overview);
  const dayTitle = single
    ? (range.key === "today" ? "Today, at a glance" : range.key === "yesterday" ? "Yesterday, at a glance" : "The day, at a glance")
    : "The week, at a glance";

  /* The page addresses its reader. The viewer's own clock is the honest one
     for a greeting — it's their morning, wherever the org's timezone sits. */
  const userName = `${user?.firstName || ""}`.trim()
    || `${user?.name || ""}`.trim().split(" ")[0]
    || (user?.email ? user.email.split("@")[0] : "");
  const hourNow = new Date().getHours();
  const dayPart = hourNow < 12 ? "morning" : hourNow < 17 ? "afternoon" : "evening";
  const dayLine = overview?.range
    ? (single ? prettyDay(overview.range.start) : `${overview.range.start} – ${overview.range.end}`)
    : "loading range";

  return (
    <div
      style={{
        minHeight: "100%",
        background: ppr.canvas,
        padding: "22px 26px 80px",
        fontFamily: t.font.family,
        color: t.c.text,
        // This page paints its own palette (viz.paper) — in light mode a muted
        // blue-grey canvas, NOT the near-white --color-bg-base the global tokens
        // assume. Publish it so the shared filter-bar chips can sit ON this page
        // instead of dropping pure-white blocks onto it. Every consumer falls
        // back to the global token, so no other screen changes.
        "--tgw-card": ppr.card,
        "--tgw-well": ppr.well,
        // borderStrong, not border: on THIS canvas the normal border (#DCE0E7)
        // is the same tone as the page (#D4DBE6), so chip edges disappeared and
        // the whole bar read as one mush. borderStrong (#BFC5CF) is the first
        // value that actually draws an edge here.
        "--tgw-line": t.c.borderStrong,
        // Hover lifts to the card surface — brighter than the resting chip, so
        // the change is unmistakable without the resting state being stark white.
        "--tgw-raise": t.c.surface,
      }}
    >
      <ClassicMotion />
      <WowMotion />

      <div style={{ maxWidth: t.layout.contentMax, margin: "0 auto" }}>
      <div className="tgw-a" style={{
        display: "flex", alignItems: "flex-end", justifyContent: "space-between",
        gap: 16, flexWrap: "wrap", marginBottom: 18,
      }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.4, textTransform: "uppercase", color: k.blue }}>
            TeamGrid · work picture
          </div>
          <h1 style={{ margin: "4px 0 0", fontSize: 28, fontWeight: 650, letterSpacing: "-0.8px" }}>
            {userName ? `Good ${dayPart}, ${userName}` : dayTitle}
          </h1>
          <div style={{ fontSize: 13, color: t.c.textTertiary, marginTop: 8, display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
              <i className="tgw-live" /> Live
            </span>
            {userName ? (<><span>·</span>{dayTitle.toLowerCase()}</>) : null}
            <span>·</span>
            {dayLine}
            {overview?.range?.timezone && (<><span>·</span>{overview.range.timezone}</>)}
          </div>
        </div>
        <div className="tg-filterbar" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <DashViewSwitch value="graphical" />
          <div style={{
            display: "inline-flex", padding: 4, borderRadius: 12,
            background: ppr.well, border: `1px solid ${t.c.border}`,
          }}>
            {PERIOD_PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                aria-pressed={range.key === p.key}
                onClick={() => setRange(rangeForPreset(p.key))}
                style={{
                  padding: "7px 13px", borderRadius: 9, fontSize: 12.5, fontWeight: 650,
                  border: 0, cursor: "pointer", fontFamily: t.font.family,
                  background: range.key === p.key ? ppr.card : "transparent",
                  color: range.key === p.key ? t.c.text : t.c.textSecondary,
                  boxShadow: range.key === p.key ? `0 1px 3px ${t.c.shadowColor}` : "none",
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
          <ToolSelect
            icon={<Users size={14} color={k.blue} />}
            value={member}
            options={memberOptions}
            onChange={selectMember}
            placeholder="All team members"
          />
          <Popover
            trigger="click"
            open={periodOpen}
            onOpenChange={setPeriodOpen}
            placement="bottomRight"
            arrow={false}
            content={
              <DateRange
                range={range}
                onChange={(r) => { setRange(r); setPeriodOpen(false); }}
                layoutId="graphicalRangeChip"
                maxSpanDays={62}
                maxDate={new Date()}
                pickerWidth={260}
                presets={PERIOD_PRESETS}
              />
            }
            overlayInnerStyle={{
              padding: t.s[3], background: t.c.surface,
              border: `1px solid ${t.c.border}`, borderRadius: t.r.md, boxShadow: t.shadow.lg,
            }}
          >
            <button className={`tg-tool ${periodOpen ? "is-open" : ""}`}>
              <CalendarRange size={14} color={k.teal} />
              <span className="tg-tool-value">Custom</span>
              <ChevronDown size={13} />
            </button>
          </Popover>
          {(showEmployeeActivity || showShiftTimeline || showGmail) && (
            <span className="tg-tool-sep" aria-hidden="true" />
          )}
          {showEmployeeActivity && (
            <button
              type="button"
              className="tg-tool tg-action"
              onClick={() => setEmployeeActivityOpen(true)}
              title="Employee activity — what the team is on right now"
            >
              <ActivityIcon size={14} color={k.violet} />
              <span className="tg-tool-value">Activity</span>
            </button>
          )}
          {showShiftTimeline && (
            <button
              type="button"
              className="tg-tool tg-action"
              onClick={() => setShiftTimelineOpen(true)}
              title="Shift timeline — clock in/out for the whole team"
            >
              <CalendarClock size={14} color={k.gold} />
              <span className="tg-tool-value">Shift Timeline</span>
            </button>
          )}
          {showGmail && (
            <button
              type="button"
              className="tg-tool tg-action"
              onClick={() => setGmailOpen(true)}
              title={gmailLoading
                ? "Gmail connections"
                : `${gmailConnected} of ${gmailTotal} members have Gmail connected`}
            >
              <GmailMark size={15} />
              <span className="tg-tool-value" style={{ fontVariantNumeric: "tabular-nums" }}>
                {gmailLoading ? "Gmail…" : (
                  <>
                    <span style={{ fontWeight: 650 }}>{gmailConnected}</span>
                    <span style={{ opacity: 0.55 }}>/{gmailTotal}</span> Gmail
                  </>
                )}
              </span>
              {!gmailLoading && <span className="tg-action-dot" data-state={gmailState} />}
            </button>
          )}
          {/* Personal, so it lives with the other personal chrome rather than as
              a strip above the org headline. Self-hiding: renders null when the
              org has the clock off or the viewer is not a stealth-agent user. */}
          <ShiftChip t={t} />
          {userName ? (
            <span
              title={`Signed in as ${userName}`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "5px 12px 5px 6px", borderRadius: 999,
                background: ppr.card, border: `1px solid ${t.c.border}`,
              }}
            >
              <MonoTile t={t} name={userName} size={22} shape="circle" />
              <span style={{ fontSize: 12.5, fontWeight: 650, color: t.c.text }}>{userName}</span>
            </span>
          ) : null}
          {/* <button
            type="button"
            onClick={() => go(classicPath(range, member))}
            style={{
              border: `1px solid ${t.c.border}`, background: t.c.surface, color: t.c.textSecondary,
              fontSize: 12.5, fontWeight: 500, padding: "7px 13px", borderRadius: 9,
              cursor: "pointer", fontFamily: t.font.family,
            }}
          >
            Card view
          </button> */}
        </div>
      </div>

      <div>
        {error ? (
          <GPanel t={t}>
            <div style={{ padding: 26, textAlign: "center", color: t.c.textTertiary, fontSize: 13 }}>
              The dashboard read failed. {String(error)}
            </div>
          </GPanel>
        ) : (
          <>
            <HeroBand
              t={t}
              overview={overview}
              loading={loading}
              livePeople={livePeople}
              liveLoading={liveLoading}
              onDay={(d) => d?.dateLocal && go(dayPath(d.dateLocal, range, member))}
              /* footer={isLead ? (
                <button
                  type="button"
                  onClick={() => go(orgTodayPath())}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, width: "100%",
                    padding: "13px 24px", border: 0, background: "none",
                    cursor: "pointer", fontFamily: t.font.family, color: t.c.text,
                    textAlign: "left", transition: "background .15s ease",
                    borderRadius: "0 0 24px 24px",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = `${k.violet}14`; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
                >
                  <i className="tgw-live" style={{ background: k.violet }} />
                  <span style={{ fontSize: 13.5, fontWeight: 700, flexShrink: 0 }}>
                    Know your organization
                  </span>
                  <span style={{ fontSize: 12.5, color: t.c.textSecondary, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    — today’s story of the whole team, written while you read
                  </span>
                  <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 700, color: k.violet, whiteSpace: "nowrap", flexShrink: 0 }}>
                    Open ›
                  </span>
                </button>
              ) : null} */
            />

            {isLead && (
              <div className="tgw-a" style={{ marginBottom: 12 }}>
                <NewsPanel
                  t={t}
                  admin
                  overview={overview}
                  loading={loading}
                  range={range}
                  member={member}
                  onOpen={(row) => row?.drill && openSubject(row.drill)}
                />
              </div>
            )}

            {!single && (
              <div className="tgw-a tgw-a3 tgw-pair" style={{ display: "grid", gridTemplateColumns: "minmax(0,1.15fr) minmax(0,.85fr)", alignItems: "start", gap: 12, marginBottom: 12 }}>
                <ClockPanel t={t} overview={overview} loading={loading} />
                <PeoplePanel
                  t={t}
                  people={peopleData.rows}
                  total={peopleData.total}
                  loading={peopleData.loading}
                  onIdle={openIdle}
                  page={peopleData.page}
                  pages={peopleData.pages}
                  onPage={peopleData.setPage}
                  pageSize={peopleData.pageSize}
                  onExport={peopleData.exportPeople}
                  onPerson={(pp) => openSubject({ kind: "person", id: pp.userId, name: pp.name })}
                />
              </div>
            )}

            <div className={`tgw-a ${single ? "tgw-a3" : "tgw-a4"} tgw-pair2`} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", alignItems: "stretch", gap: 12, marginBottom: 12 }}>
              {single && (
                <PeoplePanel
                  t={t}
                  people={peopleData.rows}
                  total={peopleData.total}
                  loading={peopleData.loading}
                  onIdle={openIdle}
                  page={peopleData.page}
                  pages={peopleData.pages}
                  onPage={peopleData.setPage}
                  pageSize={peopleData.pageSize}
                  onExport={peopleData.exportPeople}
                  onPerson={(pp) => openSubject({ kind: "person", id: pp.userId, name: pp.name })}
                />
              )}
              <ToolsPanel t={t} overview={overview} loading={loading} onTool={openTool} matchPeople={single} />
              {!single && <ProjectsPanelG t={t} overview={overview} loading={loading} onProject={openProject} />}
            </div>

            <div
              className={`tgw-a ${single ? "tgw-a4 tgw-pair2" : ""}`}
              style={{
                display: "grid",
                gridTemplateColumns: (single || isLead) ? "minmax(0,1fr) minmax(0,1fr)" : "minmax(0,1fr)",
                alignItems: "stretch",
                gap: 12, marginBottom: 12,
                ...(single ? null : { animationDelay: ".34s" }),
              }}
            >
              <AiAdoptionPanel
                t={t}
                data={aiAdopt.data}
                loading={aiAdopt.loading}
                onPerson={(pp) => openSubject({ kind: "person", id: pp.userId, name: pp.name })}
              />
              {single && <ProjectsPanelG t={t} overview={overview} loading={loading} onProject={openProject} />}
              {!single && isLead && (
                <AlertsPanel
                  t={t}
                  admin
                  overview={overview}
                  loading={loading}
                  range={range}
                  member={member}
                  onOpen={openAlert}
                />
              )}
            </div>

            {single && isLead && (
              <div className="tgw-a" style={{ marginBottom: 12 }}>
                <AlertsPanel
                  t={t}
                  admin
                  overview={overview}
                  loading={loading}
                  range={range}
                  member={member}
                  onOpen={openAlert}
                />
              </div>
            )}

            <div style={{
              marginTop: 22, textAlign: "center", fontSize: 11.5, color: t.c.textTertiary, lineHeight: 1.7,
            }}>
              Active and idle only. Nothing invented. Every picture is a door.
            </div>
          </>
        )}
      </div>
      </div>

      {/* Header-action overlays. Each renders nothing until its own gate passes. */}
      {showEmployeeActivity && (
        <EmployeeActivityModal
          open={employeeActivityOpen}
          onClose={() => setEmployeeActivityOpen(false)}
        />
      )}
      {showShiftTimeline && (
        <ShiftTimelineDrawer
          open={shiftTimelineOpen}
          onClose={() => setShiftTimelineOpen(false)}
        />
      )}
      {showGmail && (
        <GmailModal open={gmailOpen} onClose={() => setGmailOpen(false)} gmail={gmail} />
      )}

      {/* Idle drill-down — plain logs, or employee reasons when the org opted in */}
      <IdleLogsModal
        isOpen={!!idleTarget}
        onClose={() => setIdleTarget(null)}
        userId={idleTarget?.userId}
        memberName={idleTarget?.name}
      />
      <StealthIdleReasonModal
        isOpen={!!stealthIdleTarget}
        onClose={() => setStealthIdleTarget(null)}
        userId={stealthIdleTarget?.userId}
        memberName={stealthIdleTarget?.name}
        onFallbackToRegular={fallbackToRegularIdle}
      />
    </div>
  );
}
